import { SortColumn } from "react-data-grid";

import { ColorConstants } from "../../../../../../../../constants";
import { ResultType } from "../../../../../../../../models";
import { GetProjectIssuancesRequest, GetProjectIssuancesResponse } from "../../../../../../../../service/publicquery";
import { ResultData } from "../../../../../../../../service/Shared";
import {
  getHabitatFromStandardSpecificIssuanceData,
  getInventoryColumns,
  getVintageLabel,
  mapInventoryIssuancesToRows,
} from "../../../../../../../../utils";
import { DataGridColumnDefinition } from "../../../../../../../../widget";
import {
  PRE_ACCESS_DEFAULT_BIODIVERSITY_SORT_COLUMN,
  PRE_ACCESS_DEFAULT_CARBON_SORT_COLUMN,
  PRE_ACCESS_VERIFIED_STATUS,
} from "../constants/preAccessInventoryTab.constants";
import { PreAccessInventoryChartData } from "../models/preAccessInventoryTab.models";

const isVerifiedIssuance = (issuance: GetProjectIssuancesResponse["results"][number]): boolean => {
  return issuance.verificationStatus === PRE_ACCESS_VERIFIED_STATUS;
};

export const getPreAccessColumns = (
  unitMeasurement: string,
  showPrice: boolean,
  showAvailable: boolean
): DataGridColumnDefinition[] => {
  return getInventoryColumns(unitMeasurement, showPrice, showAvailable);
};

export const getPreAccessDefaultSortingCriteria = (unitMeasurement: string): SortColumn[] => {
  return [
    {
      columnKey: unitMeasurement === "Biodiversity" ? PRE_ACCESS_DEFAULT_BIODIVERSITY_SORT_COLUMN : "vintage",
      direction: "ASC",
    },
  ];
};

export const getPreAccessDefaultSortKey = (
  unitMeasurement: string
): NonNullable<GetProjectIssuancesRequest["sort"]>[number]["key"] => {
  return unitMeasurement === "Biodiversity"
    ? PRE_ACCESS_DEFAULT_BIODIVERSITY_SORT_COLUMN
    : PRE_ACCESS_DEFAULT_CARBON_SORT_COLUMN;
};

export const mapPreAccessIssuancesToRows = (
  issuances: GetProjectIssuancesResponse["results"],
  unitMeasurement: string
): ResultData[] => {
  return mapInventoryIssuancesToRows(issuances, unitMeasurement);
};

export const buildPreAccessChartData = (
  issuances: GetProjectIssuancesResponse["results"],
  unitMeasurement: string,
  showAvailable: boolean
): PreAccessInventoryChartData => {
  const xAxisLabels: (string | string[])[] = [];
  const verifiedTotal: number[] = [];
  const verifiedAvailable: number[] = [];
  const pendingTotal: number[] = [];
  const pendingAvailable: number[] = [];

  if (unitMeasurement === "Biodiversity") {
    const groupedResults: Record<string, { unitQuantityTotal: number; unitQuantityAvailable: number }> = {};

    issuances.forEach((issuance) => {
      const habitatKey = getHabitatFromStandardSpecificIssuanceData(issuance.standardSpecificIssuanceData).join("-");

      if (!groupedResults[habitatKey]) {
        groupedResults[habitatKey] = {
          unitQuantityTotal: 0,
          unitQuantityAvailable: 0,
        };
      }

      groupedResults[habitatKey].unitQuantityTotal += issuance.unitQuantityTotal;
      groupedResults[habitatKey].unitQuantityAvailable += issuance.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((habitatKey) => {
      xAxisLabels.push(habitatKey.split("-"));
      verifiedTotal.push(groupedResults[habitatKey].unitQuantityTotal);
      if (showAvailable) verifiedAvailable.push(groupedResults[habitatKey].unitQuantityAvailable);
      pendingTotal.push(0);
      pendingAvailable.push(0);
    });
  } else {
    issuances.forEach((issuance) => {
      const verified = isVerifiedIssuance(issuance);

      xAxisLabels.push(getVintageLabel(issuance));
      verifiedTotal.push(verified ? issuance.unitQuantityTotal : 0);
      pendingTotal.push(verified ? 0 : issuance.unitQuantityTotal);
      if (showAvailable) {
        verifiedAvailable.push(verified ? issuance.unitQuantityAvailable : 0);
        pendingAvailable.push(verified ? 0 : issuance.unitQuantityAvailable);
      }
    });
  }

  const pendingAllocated = pendingTotal.map((total, index) =>
    showAvailable && total === pendingAvailable[index] ? 0 : total - (showAvailable ? pendingAvailable[index] : 0)
  );
  const verifiedAllocated = verifiedTotal.map((total, index) =>
    showAvailable && total === verifiedAvailable[index] ? 0 : total - (showAvailable ? verifiedAvailable[index] : 0)
  );

  return {
    xAxisLabels,
    stackedChartData: [
      {
        label: "Allocated",
        data: pendingAllocated,
        backgroundColor: ColorConstants.INDIGO_500,
        hoverBackgroundColor: ColorConstants.INDIGO_400,
      },
      {
        label: "Available",
        data: pendingAvailable,
        backgroundColor: ColorConstants.INDIGO_100,
        hoverBackgroundColor: ColorConstants.BLUE_100,
      },
      {
        label: "Allocated",
        data: verifiedAllocated,
        backgroundColor: ColorConstants.GREEN_600,
        hoverBackgroundColor: ColorConstants.GREEN_500,
      },
      {
        label: "Available",
        data: verifiedAvailable,
        backgroundColor: ColorConstants.GREEN_200,
        hoverBackgroundColor: ColorConstants.GREEN_100,
      },
    ],
  };
};

export const buildEmptyResultType = (pageSize: number): ResultType => ({
  resultData: [],
  paging: {
    pageSize,
    totalCount: 0,
    startCursor: "",
    endCursor: "",
    hasNextPage: false,
    hasPreviousPage: false,
  },
});
